import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import LoginPage from './components/LoginPage';
import SetupPage from './components/SetupPage';
import Layout from './components/Layout';
import UserManagementPage from './components/UserManagementPage';
import AISettingsPage from './components/AISettingsPage';
import LoginAuditsPage from './components/LoginAuditsPage';
import PendingUpdatesPage from './components/PendingUpdatesPage';
import LicenseHub from './components/LicenseHub';
import SiteMonitoringPage from './components/SiteMonitoringPage';
// Website Management Components
import InukaUsersManager from './components/website/InukaUsersManager';
import PricingPlansManager from './components/website/PricingPlansManager';
import PaymentSettingsManager from './components/website/PaymentSettingsManager';
import WebsiteContentManager from './components/website/WebsiteContentManager';
import AuditLogViewer from './components/website/AuditLogViewer';
import BlogsManager from './components/website/BlogsManager';
import { isDashboardPage, readStoredPage, storePage } from './utils/pageStorage';
import './styles/DashboardTypography.css';
import './index.css';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, needsSetup, checkSetupStatus } = useAuth();
  const [currentPage, setCurrentPage] = useState(readStoredPage);

  const handlePageChange = useCallback((page: string) => {
    if (!isDashboardPage(page)) {
      return;
    }
    setCurrentPage(page);
    storePage(page);
  }, []);

  useEffect(() => {
    storePage(currentPage);
  }, [currentPage]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e2e8f0',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#64748b', margin: 0 }}>Loading...</p>
      </div>
    );
  }

  if (needsSetup) {
    return <SetupPage onSetupComplete={checkSetupStatus} />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'licenses':
        return <LicenseHub />;
      case 'users':
        return <UserManagementPage />;
      case 'audits':
        return <LoginAuditsPage />;
      case 'siteMonitoring':
        return <SiteMonitoringPage />;
      case 'pendingUpdates':
        return <PendingUpdatesPage />;
      case 'aiSettings':
        return <AISettingsPage />;
      case 'inukaUsers':
        return <InukaUsersManager />;
      case 'pricingPlans':
        return <PricingPlansManager />;
      case 'paymentSettings':
        return <PaymentSettingsManager />;
      case 'websiteContent':
        return <WebsiteContentManager />;
      case 'blogs':
        return <BlogsManager />;
      case 'websiteAuditLogs':
        return <AuditLogViewer />;
      default:
        return <LicenseHub />;
    }
  };

  return (
    <Layout currentPage={currentPage} onPageChange={handlePageChange}>
      {renderPage()}
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;
